import { AuthApiError, CreateOrganizationDialog, useAuth } from "@cavort/auth";
import {
	RiComputerLine,
	RiExpandUpDownLine,
	RiLogoutBoxLine,
	RiMoonLine,
	RiNotification4Line,
	RiPaletteLine,
	RiSearchLine,
	RiSunLine,
	RiUserLine,
} from "@remixicon/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
	useMatches,
	useRouter,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import { AppBreadcrumb } from "@/components/app-breadcrumb";
import { CommandPalette } from "@/components/command-palette";
import { OrgSwitcher } from "@/components/org-switcher";
import { SyncBar } from "@/components/sync/sync-bar";
import { useTheme } from "@/components/theme-provider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarRail,
	SidebarTrigger,
} from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { fetchCurrentUser } from "@/lib/auth";
import { buildNavGroups } from "@/lib/nav";
import { isOrgAdmin } from "@/lib/role";

interface OrgEntry {
	id: string;
	name: string;
	slug?: string;
	logo?: string;
	role: string;
	memberCount?: number;
	active?: boolean;
}

export const Route = createFileRoute("/_app")({
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!user) {
			throw redirect({ to: "/login" });
		}
		return { user };
	},
	notFoundComponent: () => {
		return (
			<div className="flex items-center justify-center p-8">
				<p className="text-muted-foreground">Page not found</p>
			</div>
		);
	},
	component: AppLayout,
});

function getInitials(name?: string, email?: string): string {
	if (name) {
		const [first, second] = name.split(" ").filter(Boolean);
		if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
		return name.slice(0, 2).toUpperCase();
	}
	if (email) return email.slice(0, 2).toUpperCase();
	return "U";
}

// ── Route label map for breadcrumbs ──────────────────────────────────
const routeLabels: Record<string, string> = {
	"/_app/dashboard": "Dashboard",
	"/_app/orders": "Orders",
	"/_app/orders/$orderId": "Order",
	"/_app/notifications": "Notifications",
	"/_app/catalogue/import": "Prohandel",
	"/_app/catalogue/matching": "Matching",
	"/_app/catalogue/editor": "Editor",
	"/_app/catalogue/export": "Farfetch",
	"/_app/catalogue/stock": "Farfetch Stock",
	"/_app/integrations": "Integrations",
	"/_app/integrations/prohandel": "Prohandel",
	"/_app/integrations/farfetch": "Farfetch",
	"/_app/settings": "Settings",
	"/_app/profile": "Profile",
	"/_app/admin": "Admin Panel",
	"/_app/onboarding": "Onboarding",
};

function AppLayout() {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { setMode, colorTheme, setColorTheme } = useTheme();
	const [createOrgOpen, setCreateOrgOpen] = useState(false);
	const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
	const matches = useMatches();

	// Cmd+K handler
	useEffect(() => {
		function onKeyDown(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				setCmdPaletteOpen((v) => !v);
			}
		}
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, []);

	// Full profile from cavort-auth's /auth/me (cached by useAuth).
	// `logout` revokes the server session and clears the query cache.
	const { user: profile, logout } = useAuth();

	// User's organizations for the switcher (cavort-auth endpoint).
	const { data: orgsData } = useQuery({
		queryKey: ["auth", "organizations"],
		queryFn: async () => {
			const res = await fetch("/auth/organizations", { credentials: "include" });
			if (!res.ok) return [] as OrgEntry[];
			const body = (await res.json()) as { organizations: OrgEntry[] };
			return body.organizations ?? [];
		},
		staleTime: 30_000,
	});

	// Unread notification count for the bell. Read from the purpose-built
	// endpoint rather than counting client-side over `/notifications`: that
	// returns a paginated envelope (not an array), and one page would
	// undercount anyway once an org passes the page limit.
	const { data: unreadData } = $api.useQuery("get", "/api/v1/notifications/unread-count");
	const unreadCount = unreadData?.count ?? 0;

	const orgs = orgsData ?? [];
	const displayName = profile?.name ?? "User";
	const displayEmail = profile?.email;
	const initials = getInitials(profile?.name, displayEmail);
	// cavort-auth's /auth/me serialises the active org as `active_org_id`
	// (snake_case) even though the `AuthUser` type declares `organizationId`.
	// Until cavort-auth aligns the wire shape, derive the active org from
	// the orgs list (where each entry already carries `active: boolean`).
	const currentOrgId = orgs.find((o) => o.active)?.id ?? "";
	const userRole = profile?.role ?? "member";
	const isAdmin = profile?.isAdmin ?? false;
	const navGroups = buildNavGroups(isOrgAdmin(profile));

	async function handleLogout() {
		try {
			await logout();
		} catch (e) {
			// 401 = the session had already ended (the library refreshed and
			// retried once): the user is logged out either way. Anything else
			// means the server did not revoke the session, so say so and stay.
			if (!(e instanceof AuthApiError && e.status === 401)) {
				toast.error("Logout failed. Please try again.");
				return;
			}
		}
		await router.navigate({ to: "/login" });
	}

	async function handleSwitchOrg(orgId: string) {
		const res = await fetch("/auth/organizations/switch", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ organization_id: orgId }),
		});
		if (!res.ok) {
			toast.error("Failed to switch organization");
			return;
		}
		queryClient.clear();
		window.location.href = "/dashboard";
	}

	async function handleOrgCreated(created: { id: string; name: string }) {
		toast.success(`Created "${created.name}"`);
		await handleSwitchOrg(created.id);
	}

	return (
		<TooltipProvider>
			<SidebarProvider>
				<Sidebar collapsible="icon">
					<SidebarHeader>
						<OrgSwitcher
							orgs={orgs}
							currentOrgId={currentOrgId}
							userRole={userRole}
							isSuperadmin={isAdmin}
							onSwitchOrg={handleSwitchOrg}
							onCreateOrg={() => setCreateOrgOpen(true)}
						/>
					</SidebarHeader>
					<SidebarContent>
						{navGroups.map((group) => (
							<SidebarGroup key={group.label}>
								<SidebarGroupLabel>{group.label}</SidebarGroupLabel>
								<SidebarGroupContent>
									<SidebarMenu>
										{group.items.map((item) => (
											<SidebarMenuItem key={item.to}>
												<SidebarMenuButton tooltip={item.label} asChild>
													<Link to={item.to}>
														<item.icon className="size-4" />
														<span>{item.label}</span>
													</Link>
												</SidebarMenuButton>
											</SidebarMenuItem>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</SidebarGroup>
						))}
					</SidebarContent>
					<SidebarFooter>
						<SidebarMenu>
							<SidebarMenuItem>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<SidebarMenuButton
											size="lg"
											className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
										>
											<Avatar className="size-8 rounded-lg">
												<AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
											</Avatar>
											<div className="grid flex-1 text-left text-sm leading-tight">
												<span className="truncate font-medium">{displayName}</span>
												{displayEmail && (
													<span className="truncate text-xs text-muted-foreground">
														{displayEmail}
													</span>
												)}
											</div>
											<RiExpandUpDownLine className="ml-auto size-4" />
										</SidebarMenuButton>
									</DropdownMenuTrigger>
									<DropdownMenuContent side="top" align="end" sideOffset={4}>
										<DropdownMenuGroup>
											<DropdownMenuLabel className="p-0 font-normal">
												<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
													<Avatar className="size-8 rounded-lg">
														<AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
													</Avatar>
													<div className="grid flex-1 text-left text-sm leading-tight">
														<span className="truncate font-medium">{displayName}</span>
														{displayEmail && (
															<span className="truncate text-xs text-muted-foreground">
																{displayEmail}
															</span>
														)}
													</div>
												</div>
											</DropdownMenuLabel>
										</DropdownMenuGroup>
										<DropdownMenuSeparator />
										<DropdownMenuGroup>
											<DropdownMenuItem asChild>
												<Link to="/profile">
													<RiUserLine className="size-4" />
													Profile
													<DropdownMenuShortcut>&#8679;&#8984;P</DropdownMenuShortcut>
												</Link>
											</DropdownMenuItem>
										</DropdownMenuGroup>
										<DropdownMenuSeparator />
										<DropdownMenuGroup>
											<DropdownMenuSub>
												<DropdownMenuSubTrigger>
													<RiSunLine className="size-4" />
													Theme
												</DropdownMenuSubTrigger>
												<DropdownMenuSubContent>
													<DropdownMenuItem onClick={() => setMode("light")}>
														<RiSunLine className="size-4" />
														Light
													</DropdownMenuItem>
													<DropdownMenuItem onClick={() => setMode("dark")}>
														<RiMoonLine className="size-4" />
														Dark
													</DropdownMenuItem>
													<DropdownMenuItem onClick={() => setMode("system")}>
														<RiComputerLine className="size-4" />
														System
													</DropdownMenuItem>
												</DropdownMenuSubContent>
											</DropdownMenuSub>
											<DropdownMenuSub>
												<DropdownMenuSubTrigger>
													<RiPaletteLine className="size-4" />
													Color
												</DropdownMenuSubTrigger>
												<DropdownMenuSubContent>
													{(
														[
															{
																key: "blue",
																label: "Blue",
																color: "oklch(0.488 0.243 264.376)",
															},
															{
																key: "green",
																label: "Green",
																color: "oklch(0.627 0.164 164.36)",
															},
															{
																key: "orange",
																label: "Orange",
																color: "oklch(0.69 0.204 70.746)",
															},
															{
																key: "red",
																label: "Red",
																color: "oklch(0.637 0.237 25.331)",
															},
															{
																key: "rose",
																label: "Rose",
																color: "oklch(0.645 0.246 16.439)",
															},
															{
																key: "violet",
																label: "Violet",
																color: "oklch(0.627 0.265 303.9)",
															},
															{
																key: "yellow",
																label: "Yellow",
																color: "oklch(0.769 0.188 70.08)",
															},
															{
																key: "neutral",
																label: "Neutral",
																color: "oklch(0.556 0 0)",
															},
														] as const
													).map((t) => (
														<DropdownMenuItem key={t.key} onClick={() => setColorTheme(t.key)}>
															<span
																className="size-3 rounded-full shrink-0"
																style={{ background: t.color }}
															/>
															{t.label}
															{colorTheme === t.key && (
																<span className="ml-auto text-xs">&#10003;</span>
															)}
														</DropdownMenuItem>
													))}
												</DropdownMenuSubContent>
											</DropdownMenuSub>
										</DropdownMenuGroup>
										<DropdownMenuSeparator />
										<DropdownMenuItem onClick={handleLogout}>
											<RiLogoutBoxLine className="size-4" />
											Sign out
											<DropdownMenuShortcut>&#8679;&#8984;S</DropdownMenuShortcut>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarFooter>
					<SidebarRail />
				</Sidebar>
				<SidebarInset className="min-w-0 overflow-x-hidden">
					<header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
						<SidebarTrigger />
						<Separator orientation="vertical" className="h-6" />
						<AppBreadcrumb
							items={matches.flatMap((m) => {
								const label = routeLabels[m.id];
								return label ? [{ label, href: m.pathname }] : [];
							})}
							className="flex-1"
						/>
						<div className="ml-auto flex items-center gap-1">
							<button
								type="button"
								onClick={() => setCmdPaletteOpen(true)}
								className="inline-flex items-center gap-2 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted transition-colors"
							>
								<RiSearchLine className="size-3.5" />
								<span className="hidden sm:inline">Search</span>
								<kbd className="rounded border bg-background px-1 py-0.5 font-mono text-[10px]">
									&#8984;K
								</kbd>
							</button>
							<Link
								to="/notifications"
								className="relative inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
							>
								<RiNotification4Line className="size-4" />
								{unreadCount > 0 && (
									<Badge
										variant="destructive"
										className="absolute -top-0.5 -right-0.5 size-4 items-center justify-center rounded-full p-0 text-[10px]"
									>
										{unreadCount > 9 ? "9+" : unreadCount}
									</Badge>
								)}
							</Link>
						</div>
					</header>
					<SyncBar />
					<div className="flex-1 p-6 min-w-0">
						<Outlet />
					</div>
				</SidebarInset>
				<CreateOrganizationDialog
					open={createOrgOpen}
					onOpenChange={setCreateOrgOpen}
					onCreated={handleOrgCreated}
				/>
				<CommandPalette open={cmdPaletteOpen} onOpenChange={setCmdPaletteOpen} />
			</SidebarProvider>
		</TooltipProvider>
	);
}
